import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { SearchField, SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { getPage } from "../lib/api";
import {
  attemptHeadline,
  attemptHint,
  attemptTone,
  connectionSpan,
  isStationIdFilter,
} from "../lib/connectionAttempts";
import { count, since } from "../lib/format";
import { protocolLabel } from "../lib/protocol";
import {
  CONNECTION_ATTEMPT_OUTCOMES,
  type ConnectionAttempt,
  type ConnectionAttemptOutcome,
} from "../lib/types";

/**
 * Every handshake at `/ocpp/{id}` in the last day, live.
 *
 * ## What this page is for
 *
 * An installer at a station that will not come online. The frames on a station's
 * own page start after the handshake, and the handshake is the part that fails
 * during an install: the wrong URL, the wrong password, a version this server does
 * not speak. Those attempts were log lines; this is them, readable, refreshing
 * every two seconds while someone reconfigures the station.
 *
 * ## Why it can pause
 *
 * A table that reorders under the pointer every two seconds cannot be read during
 * a retry storm. Pausing stops the polling and keeps the rows still; the masthead
 * says so, because a frozen live view that looks live is worse than none.
 *
 * ## Why it forgets
 *
 * The server keeps a day and prunes the rest (`connection_attempt`, UNLOGGED).
 * The page says so in its lede rather than leaving an operator to wonder where
 * last week went.
 */

const LIVE_POLL_MS = 2_000;
/** The masthead count moves slowly and costs a count over the table; no need for 2s. */
const COUNT_POLL_MS = 10_000;

const COLUMNS: readonly Column[] = [
  { key: "when", label: "When", width: "10%" },
  { key: "station", label: "Station", width: "16%" },
  { key: "outcome", label: "Outcome" },
  { key: "from", label: "From", width: "20%" },
  { key: "offer", label: "Offered", width: "16%" },
];

const OUTCOME_LABELS: Readonly<Record<ConnectionAttemptOutcome, string>> = {
  accepted: "accepted",
  unsupported_protocol: "no common version",
  rejected: "rejected",
};

export function ConnectionAttempts() {
  const [params, setParams] = useSearchParams();
  const [isPaused, setPaused] = useState(false);

  const chargePointId = params.get("chargePointId") ?? "";
  const outcome = params.get("outcome") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;
  /**
   * What is typed, kept apart from the filter in the URL: a half-typed id with a
   * space in it is not sent, so the server's 400 is never polled every two seconds.
   */
  const [stationDraft, setStationDraft] = useState(chargePointId);
  const isStationDraftValid = stationDraft === "" || isStationIdFilter(stationDraft);
  // Paused means still: neither the timer nor the tab regaining focus refetches.
  const refetchInterval = isPaused ? false : LIVE_POLL_MS;
  const refetchOnWindowFocus = !isPaused;

  const attempts = useQuery({
    queryKey: ["connection-attempts", { chargePointId, outcome, page }],
    queryFn: () =>
      getPage<ConnectionAttempt>("/connection-attempts", {
        query: { chargePointId, outcome, page },
      }),
    placeholderData: (previous) => previous,
    refetchInterval,
    refetchOnWindowFocus,
  });

  /** Refusals in the window, whatever the table is filtered to. */
  const rejected = useQuery({
    queryKey: ["connection-attempts", "rejected-count", chargePointId],
    queryFn: () =>
      getPage<ConnectionAttempt>("/connection-attempts", {
        query: { chargePointId, outcome: "rejected", limit: 1 },
      }),
    refetchInterval: isPaused ? false : COUNT_POLL_MS,
    refetchOnWindowFocus,
  });

  const rows = attempts.data?.rows ?? [];
  const isFiltered = chargePointId !== "" || outcome !== "";

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  return (
    <>
      <PageHeader
        eyebrow="Connections"
        title={
          chargePointId === "" ? "Connection attempts" : `Connection attempts · ${chargePointId}`
        }
        metrics={
          <>
            <Metric
              label="Attempts"
              value={count(attempts.data?.meta?.total)}
              hint="last 24 hours"
            />
            <Metric
              label="Turned away"
              value={count(rejected.data?.meta?.total)}
              hint="last 24 hours"
              tone={(rejected.data?.meta?.total ?? 0) > 0 ? "signal" : "muted"}
            />
          </>
        }
        actions={
          <Button variant="ghost" onClick={() => setPaused(!isPaused)}>
            {isPaused ? "Resume live" : "Pause"}
          </Button>
        }
        freshness={
          <MicroLabel>{isPaused ? "Paused" : `Live · every ${LIVE_POLL_MS / 1000}s`}</MicroLabel>
        }
        lede="Every handshake a station made at /ocpp/{id}, including the ones turned away before they became a connection — wrong password, unregistered id, a version this server does not speak. Kept for a day; older attempts are pruned."
      />

      <form
        onSubmit={(event) => event.preventDefault()}
        className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <SearchField
          label="Station id"
          value={stationDraft}
          placeholder="CP-0001"
          hint="Exact match"
          error={
            isStationDraftValid
              ? undefined
              : "A station id is letters, digits, dot, dash and underscore only."
          }
          onChange={(event) => {
            const next = event.target.value.trim();
            setStationDraft(next);
            if (next === "" || isStationIdFilter(next)) setFilter("chargePointId", next);
          }}
        />
        <SelectField
          label="Outcome"
          value={outcome}
          onChange={(event) => setFilter("outcome", event.target.value)}
        >
          <option value="">Any</option>
          {CONNECTION_ATTEMPT_OUTCOMES.map((one) => (
            <option key={one} value={one}>
              {OUTCOME_LABELS[one]}
            </option>
          ))}
        </SelectField>
      </form>

      {attempts.isError ? (
        <ErrorNotice error={attempts.error} onRetry={() => void attempts.refetch()} />
      ) : null}

      <div className="flex flex-col gap-3">
        <TableFrame>
          <TableHead columns={COLUMNS} />
          {attempts.isPending ? (
            <SkeletonRows rows={8} columns={COLUMNS.length} />
          ) : (
            <tbody>
              {rows.map((row, index) => (
                <AttemptRow key={row.id} attempt={row} index={index} />
              ))}
            </tbody>
          )}
        </TableFrame>

        {!attempts.isPending && rows.length === 0 ? (
          <EmptyState
            title={
              isFiltered
                ? "No attempts match these filters in the last day."
                : "No station has tried to connect in the last day."
            }
            detail="A row appears the moment a station opens a WebSocket at /ocpp/{id}, whether or not it gets in. If a station you are configuring never shows up here, its request is not reaching this server at all — check the URL's host and port, and any proxy in between."
          />
        ) : null}

        <Pagination
          meta={attempts.data?.meta}
          shown={rows.length}
          onPage={(next) => {
            const nextParams = new URLSearchParams(params);
            if (next <= 1) nextParams.delete("page");
            else nextParams.set("page", String(next));
            setParams(nextParams);
          }}
        />
      </div>
    </>
  );
}

function AttemptRow({
  attempt,
  index,
}: {
  readonly attempt: ConnectionAttempt;
  readonly index: number;
}) {
  const hint = attemptHint(attempt);
  const span = connectionSpan(attempt);

  return (
    <TableRow index={index} isFaulted={attempt.outcome !== "accepted"}>
      <Cell muted>
        <time dateTime={attempt.attemptedAt} title={new Date(attempt.attemptedAt).toLocaleString()}>
          {since(attempt.attemptedAt)}
        </time>
      </Cell>
      <Cell>
        {attempt.chargePointId === null ? (
          <span className="break-all text-ink-faint">{attempt.path || "(no path)"}</span>
        ) : (
          <Link
            to={`/fleet/${encodeURIComponent(attempt.chargePointId)}`}
            className="break-all font-medium text-ink underline decoration-rule underline-offset-2 hover:decoration-signal"
          >
            {attempt.chargePointId}
          </Link>
        )}
      </Cell>
      <Cell>
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <Pill tone={attemptTone(attempt)}>{OUTCOME_LABELS[attempt.outcome]}</Pill>
            <span className="font-medium text-ink">{attemptHeadline(attempt)}</span>
            {attempt.httpStatus === null || attempt.httpStatus === 101 ? null : (
              <span className="text-micro tracking-normal normal-case text-ink-faint">
                HTTP {attempt.httpStatus}
              </span>
            )}
          </div>
          {hint === undefined ? null : (
            <span className="text-micro tracking-normal normal-case text-ink-faint">{hint}</span>
          )}
          {span === undefined ? null : (
            <span className="text-micro tracking-normal normal-case text-ink-faint">{span}</span>
          )}
        </div>
      </Cell>
      <Cell>
        <div className="flex flex-col gap-0.5">
          <span className="text-data break-all text-ink">{attempt.remoteAddress ?? "—"}</span>
          {attempt.forwardedFor === null ? null : (
            <span className="text-micro tracking-normal normal-case break-all text-ink-faint">
              forwarded for {attempt.forwardedFor}
            </span>
          )}
          {attempt.userAgent === null ? null : (
            <span className="text-micro tracking-normal normal-case break-all text-ink-faint">
              {attempt.userAgent}
            </span>
          )}
        </div>
      </Cell>
      <Cell>
        <div className="flex flex-col gap-0.5">
          <span className="text-ink">
            {attempt.offeredSubprotocols.length === 0
              ? "—"
              : attempt.offeredSubprotocols.map(protocolLabel).join(", ")}
          </span>
          <span className="text-micro tracking-normal normal-case text-ink-faint">
            auth: {attempt.authPresented.replaceAll("_", " ")}
          </span>
        </div>
      </Cell>
    </TableRow>
  );
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
